import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supportSessionApi } from '@formosa/shared/api/im';
import {
  abandonWaitingSupportSession,
  abandonWaitingSupportSessionKeepalive,
  canMemberSendInSupport,
  isTerminalSupportStatus,
  markSupportDisconnectKeepalive,
  startOrResumeSupportSession,
} from '@formosa/shared/utils/customerServiceEntry';
import MessageTimeline from '../../components/chat/MessageTimeline';
import Composer from '../../components/chat/Composer';
import TypingIndicator from '../../components/chat/TypingIndicator';
import { useChat } from '../../chat/ChatProvider';
import { useImFeature } from '../../im/ImFeatureProvider';
import SupportSessionStatusBar from './components/SupportSessionStatusBar';
import SupportSessionHistoryPanel from './components/SupportSessionHistoryPanel';
import SupportRatingCard from './components/SupportRatingCard';
import '../../styles/chat.css';
import '../../styles/supportChat.css';

/** Cancel deferred leave when Strict Mode remounts within this window. */
let pendingWaitingLeaveTimer = null;

export default function SupportChatPage() {
  const navigate = useNavigate();
  const { chatEnabled, formosaImEnabled, loaded: featureLoaded } = useImFeature();
  const { openConversation, dispatch } = useChat();
  const [session, setSession] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [inGrace, setInGrace] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const sessionRef = useRef(null);
  sessionRef.current = session;

  const applySession = useCallback(
    async (resp) => {
      if (!resp) {
        setSession(null);
        setConversationId(null);
        return;
      }
      setSession(resp);
      const cid = resp.conversationId != null ? String(resp.conversationId) : null;
      setConversationId(cid);
      if (cid) {
        dispatch({
          type: 'UPSERT_CONVERSATION',
          conversation: {
            conversationId: cid,
            id: cid,
            conversationType: 'CUSTOMER_SERVICE',
            title: '線上客服',
          },
        });
        await openConversation(cid);
      }
    },
    [dispatch, openConversation]
  );

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const page = await supportSessionApi.history({ limit: 30 });
      setHistoryItems(page?.items || []);
    } catch (err) {
      setHistoryError(err?.message || '無法載入歷史紀錄');
      setHistoryItems([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const bootstrap = useCallback(async () => {
    if (!formosaImEnabled) {
      setLoading(false);
      setError('');
      setSession(null);
      setConversationId(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const current = await supportSessionApi.current();
      if (current?.hasActiveSession && current?.sessionId) {
        await applySession(current);
      } else {
        const started = await startOrResumeSupportSession({ category: 'GENERAL' });
        await applySession(started);
      }
      setInGrace(false);
      loadHistory();
    } catch (err) {
      setError(err?.message || '無法開啟客服會話');
      setSession(null);
      setConversationId(null);
    } finally {
      setLoading(false);
    }
  }, [applySession, loadHistory, formosaImEnabled]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    if (!session?.sessionId || isTerminalSupportStatus(session.status)) {
      return undefined;
    }
    const timer = setInterval(async () => {
      try {
        const current = await supportSessionApi.current();
        if (current?.sessionId) {
          setSession(current);
          if (current.inGrace != null) {
            setInGrace(!!current.inGrace);
          }
        }
      } catch {
        /* ignore poll errors */
      }
    }, 8000);
    return () => clearInterval(timer);
  }, [session?.sessionId, session?.status]);

  // Leaving the page while WAITING → ABANDONED (queue remove). Debounced for Strict Mode.
  useEffect(() => {
    if (pendingWaitingLeaveTimer) {
      clearTimeout(pendingWaitingLeaveTimer);
      pendingWaitingLeaveTimer = null;
    }
    return () => {
      const snap = sessionRef.current;
      if (!snap?.sessionId || snap.status !== 'WAITING') return;
      pendingWaitingLeaveTimer = setTimeout(() => {
        pendingWaitingLeaveTimer = null;
        abandonWaitingSupportSessionKeepalive(snap, 'CLIENT_EXPLICIT_CLOSE');
      }, 400);
    };
  }, [session?.sessionId, session?.status]);

  // ACTIVE* tab close／reload：keepalive Grace（WS CS12 為主路徑；此為雙保險）
  useEffect(() => {
    const onPageHide = () => {
      const snap = sessionRef.current;
      markSupportDisconnectKeepalive(snap);
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  async function leaveWaitingThen(navigateFn) {
    const snap = sessionRef.current;
    if (snap?.status === 'WAITING' && snap?.sessionId) {
      setBusy(true);
      try {
        await abandonWaitingSupportSession(snap, 'CLIENT_EXPLICIT_CLOSE');
        setSession((prev) => (prev ? { ...prev, status: 'ABANDONED' } : prev));
      } catch {
        // Still navigate; keepalive cleanup may retry on unmount
        abandonWaitingSupportSessionKeepalive(snap, 'CLIENT_EXPLICIT_CLOSE');
      } finally {
        setBusy(false);
      }
    }
    navigateFn();
  }

  async function handleBack() {
    await leaveWaitingThen(() => navigate(-1));
  }

  async function handleLeave() {
    if (!session?.sessionId) return;
    setBusy(true);
    setError('');
    try {
      const resp = await supportSessionApi.leave(session.sessionId, {
        reason: 'CUSTOMER_CANCELLED',
      });
      await applySession(resp);
      loadHistory();
    } catch (err) {
      setError(err?.message || '取消失敗');
    } finally {
      setBusy(false);
    }
  }

  async function handleEnd() {
    if (!session?.sessionId) return;
    if (!window.confirm('確定結束本次客服服務？')) return;
    setBusy(true);
    setError('');
    try {
      const resp = await supportSessionApi.end(session.sessionId, {});
      setSession({
        ...session,
        status: resp?.status || 'ENDED',
        sessionId: resp?.sessionId || session.sessionId,
        conversationId: resp?.conversationId || session.conversationId,
      });
      setInGrace(false);
      loadHistory();
    } catch (err) {
      setError(err?.message || '結束失敗');
    } finally {
      setBusy(false);
    }
  }

  async function handleRecontact() {
    setBusy(true);
    setError('');
    try {
      const started = await startOrResumeSupportSession({ category: 'GENERAL' });
      await applySession(started);
      setInGrace(false);
      setSelectedHistoryId(null);
      loadHistory();
    } catch (err) {
      setError(err?.message || '再次聯絡失敗');
    } finally {
      setBusy(false);
    }
  }

  async function handleSelectHistory(item) {
    if (!item?.sessionId) return;
    setSelectedHistoryId(item.sessionId);
    setHistoryError('');
    try {
      const detail = await supportSessionApi.getSession(String(item.sessionId));
      if (detail?.conversationId != null) {
        const cid = String(detail.conversationId);
        setConversationId(cid);
        await openConversation(cid);
      }
    } catch (err) {
      setHistoryError(err?.message || '無法開啟歷史 Session');
    }
  }

  const allowComposer = canMemberSendInSupport(session?.status) && !inGrace && formosaImEnabled;

  if (featureLoaded && !formosaImEnabled) {
    return (
      <div className="chat-app has-room support-chat-page">
        <section className="chat-app__main">
          <header className="chat-app__room-header">
            <button
              type="button"
              className="chat-app__back"
              onClick={() => navigate('/support')}
              aria-label="返回"
            >
              ←
            </button>
            <div className="chat-app__room-title">
              <strong>客服中心</strong>
              <small>通道維修中</small>
            </div>
          </header>
          <div className="chat-app__empty-pane">
            <p>自建客服通道目前維修中或已下線</p>
            <p className="chat-state__hint">請返回客服中心查看可用入口</p>
            <Link to="/support" className="chat-link-btn">前往客服中心</Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="chat-app has-room support-chat-page">
      <section className="chat-app__main">
        <header className="chat-app__room-header">
          <button
            type="button"
            className="chat-app__back"
            onClick={handleBack}
            aria-label="返回"
            disabled={busy}
          >
            ←
          </button>
          <div className="chat-app__room-title">
            <strong>客服中心</strong>
            <small>即時客服</small>
          </div>
          <Link
            to="/support"
            className="chat-link-btn"
            onClick={(e) => {
              if (sessionRef.current?.status === 'WAITING') {
                e.preventDefault();
                leaveWaitingThen(() => navigate('/support'));
              }
            }}
          >
            返回
          </Link>
          {chatEnabled ? (
            <Link
              to="/chat"
              className="chat-link-btn"
              onClick={(e) => {
                if (sessionRef.current?.status === 'WAITING') {
                  e.preventDefault();
                  leaveWaitingThen(() => navigate('/chat'));
                }
              }}
            >
              聊天
            </Link>
          ) : null}
        </header>

        {error ? <div className="chat-banner chat-banner--error">{error}</div> : null}
        {loading ? <div className="chat-banner">正在連接客服…</div> : null}

        {!loading ? (
          <SupportSessionStatusBar
            session={session}
            inGrace={inGrace}
            busy={busy}
            onLeave={handleLeave}
            onEnd={handleEnd}
            onRecontact={handleRecontact}
          />
        ) : null}

        {!loading ? (
          <SupportSessionHistoryPanel
            items={historyItems}
            loading={historyLoading}
            error={historyError}
            open={historyOpen}
            onToggle={() => {
              const next = !historyOpen;
              setHistoryOpen(next);
              if (next) loadHistory();
            }}
            selectedSessionId={selectedHistoryId}
            onSelect={handleSelectHistory}
          />
        ) : null}

        {conversationId ? (
          <>
            <MessageTimeline conversationId={conversationId} />
            <TypingIndicator conversationId={conversationId} />
            <SupportRatingCard
              sessionId={session?.sessionId}
              conversationId={conversationId}
            />
            {allowComposer ? (
              <Composer conversationId={conversationId} />
            ) : (
              <div className="support-composer-fallback">
                {isTerminalSupportStatus(session?.status) ? (
                  <button type="button" className="chat-link-btn" disabled={busy} onClick={handleRecontact}>
                    再次聯絡客服
                  </button>
                ) : (
                  <span>目前無法傳送訊息</span>
                )}
              </div>
            )}
          </>
        ) : !loading ? (
          <div className="chat-app__empty-pane">
            <p>無法建立客服會話</p>
            <p className="chat-state__hint">若已切換為第三方客服，請前往客服中心</p>
            <Link to="/support" className="chat-link-btn">前往客服中心</Link>
          </div>
        ) : null}
      </section>
    </div>
  );
}
